import type { SeriesOptionsType } from 'highcharts/highstock';

import {
  getSeriesPaletteColor,
  type ChartAppearance
} from '@/features/charts/theme/chartAppearance';
import type { ExtendedSeriesOptionsType } from '@/features/charts/types/highcharts';
import { clock } from '@/services/clock';

const AREA_RANGE_FILL_OPACITY = 0.2;

const normalizePaletteIndex = (index: number, length: number): number =>
  ((index % length) + length) % length;

export const applyChartSeriesAppearance = <T extends SeriesOptionsType>(
  series: T,
  combinedIndex: number,
  appearance: ChartAppearance
): T => {
  const explicitColor = 'color' in series ? series.color : undefined;

  return {
    ...series,
    color: explicitColor ?? getSeriesPaletteColor(appearance, combinedIndex)
  };
};

const applyFallbackSeriesAppearance = (
  series: ExtendedSeriesOptionsType,
  appearance: ChartAppearance
): ExtendedSeriesOptionsType =>
  applyChartSeriesAppearance(
    {
      ...series,
      color: series.custom.color
    },
    series.custom.combinedIndex,
    appearance
  );

export const withSpotSeriesAppearance = (
  series: ExtendedSeriesOptionsType,
  appearance: ChartAppearance
): ExtendedSeriesOptionsType => applyFallbackSeriesAppearance(series, appearance);

export const withFullscreenSeriesAppearance = (
  series: ExtendedSeriesOptionsType,
  appearance: ChartAppearance
): ExtendedSeriesOptionsType => applyFallbackSeriesAppearance(series, appearance);

type StoredPaletteMatch = {
  palette: readonly string[];
  index: number;
};

const findStoredPalette = (
  color: string | undefined,
  appearance: ChartAppearance
): StoredPaletteMatch | null => {
  if (!color) return null;

  const normalizedColor = color.toUpperCase();
  const palettes = [
    appearance.seriesPalettes.vibrant,
    appearance.seriesPalettes.monoBlue,
    appearance.seriesPalettes.balanced
  ];

  for (const palette of palettes) {
    const index = palette.findIndex(entry => entry.toUpperCase() === normalizedColor);
    if (index >= 0) return { palette, index };
  }

  return null;
};

const resolveForwardCurveColor = (
  currentColor: string | undefined,
  combinedIndex: number,
  appearance: ChartAppearance
): string => {
  const match = findStoredPalette(currentColor, appearance);

  if (match && combinedIndex >= 0 && match.palette.length > 0) {
    return match.palette[normalizePaletteIndex(combinedIndex, match.palette.length)];
  }

  return getSeriesPaletteColor(appearance, combinedIndex);
};

export const withForwardCurveSeriesAppearance = (
  series: ExtendedSeriesOptionsType,
  appearance: ChartAppearance
): ExtendedSeriesOptionsType => {
  const currentColor = typeof series.color === 'string' ? series.color : undefined;

  return {
    ...series,
    color: resolveForwardCurveColor(currentColor, series.custom.combinedIndex, appearance)
  };
};

export const withSeasonalSeriesAppearance = (
  series: ExtendedSeriesOptionsType,
  appearance: ChartAppearance
): ExtendedSeriesOptionsType => {
  const { combinedIndex, field } = series.custom;

  if (series.type === 'arearange') {
    return {
      ...series,
      color: appearance.mutedForeground,
      fillOpacity: AREA_RANGE_FILL_OPACITY
    };
  }

  if (field === 'average') {
    return { ...series, color: appearance.foreground };
  }

  if (!Number.isNaN(Number(field))) {
    const yearDistance = clock.date().getFullYear() - Number(field);
    return { ...series, color: getSeriesPaletteColor(appearance, yearDistance) };
  }

  return { ...series, color: getSeriesPaletteColor(appearance, combinedIndex) };
};
